import axios from 'axios';
import { getCsrfToken } from './utils/getCsrfToken.ts';
import { DEFAULT_HEADERS } from './constants.ts';

export type { AxiosInstance, AxiosResponse, AxiosError } from 'axios';

export function stripHtml(html: string) {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim();
}

const baseURLOverride = import.meta.env.VITE_API_BASE_URL;

const instance = axios.create({
  adapter: 'fetch', // Use fetch adapter for better compatibility with modern browsers
  baseURL: baseURLOverride ?? '/',
  // timeout: 1000,
  headers: DEFAULT_HEADERS,
  withCredentials: true,
});

instance.interceptors.request.use(
  (config) => {
    // console.log(config);
    config.headers['X-CSRFToken'] = getCsrfToken();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
instance.interceptors.response.use(
  (response) => {
    const contentType = response.headers['content-type'];
    // Check if the response text contains the redirect marker.
    if (
      typeof response.data === 'string' &&
      response.data.includes('LOGIN:::REDIRECT:::LOGIN')
    ) {
      return {
        ...response,
        status: 401,
        statusText: 'failed',
        data: { error: 'Unauthorised' },
      };
    }

    const data = response.data;

    if (typeof data === 'string' && contentType?.startsWith('text/')) {
      const alertRegEx = /alert alert-block alert-danger">(.*?)<\/div>/;
      const alert = data.match(alertRegEx);

      if (alert?.[1]) {
        throw new Error(stripHtml(alert[1]));
      }

      const regex = /MESSAGE:::(.*?):::MESSAGE/;
      const message = data.match(regex)?.[1];

      return {
        ...response,
        data: { success: message ? stripHtml(message) : 'No message provided' },
      };
    }

    return response;
  },
  (error) => {
    // Optionally handle errors globally
    return Promise.reject(error);
  }
);

export default instance;
